import type { AnalyticsConsent } from './analyticsConsent'
import { createPostHogController, type PostHogClientLike } from './posthogController'
import {
  isSensitiveAnalyticsUrl,
  redactAnalyticsNetworkRequest,
  redactSensitiveAnalyticsUrl,
} from './analyticsUrl'
import { resolveWebPostHogToken } from './posthogProject'

const projectToken = resolveWebPostHogToken(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN)
const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST?.trim() || 'https://us.i.posthog.com'
const currentAnalyticsUrl = (defaultUrl?: string) =>
  redactSensitiveAnalyticsUrl(defaultUrl ?? window.location.href)
const readEffectiveAnalyticsConsent = () =>
  typeof window !== 'undefined' && isSensitiveAnalyticsUrl(window.location.href)
    ? null
    : 'granted' as const

async function loadPostHog(): Promise<PostHogClientLike> {
  const { default: posthog } = await import('posthog-js')

  return {
    init: (token, config) =>
      posthog.init(token, config as Parameters<typeof posthog.init>[1]),
    capture: (name, properties) => posthog.capture(name, properties),
    opt_in_capturing: (options) => posthog.opt_in_capturing(options),
    opt_out_capturing: () => posthog.opt_out_capturing(),
    startSessionRecording: () => posthog.startSessionRecording(),
    stopSessionRecording: () => posthog.stopSessionRecording(),
  }
}

const controller = createPostHogController({
  token: projectToken,
  readConsent: readEffectiveAnalyticsConsent,
  loadClient: loadPostHog,
  getCurrentUrl: currentAnalyticsUrl,
  config: {
    api_host: apiHost,
    defaults: '2026-05-30',
    // The controller emits one explicit $pageview after consent and persisted
    // opt-out reconciliation. SDK auto-pageviews can otherwise race opt-in and
    // double-count the first consented view.
    capture_pageview: false,
    get_current_url: currentAnalyticsUrl,
    // Defense in depth for every SDK-owned URL field. Replay rrweb Meta URLs
    // do not use get_current_url, but they do pass through this local callback.
    mask_personal_data_properties: true,
    custom_personal_data_properties: ['token', 'unsubscribe_token'],
    capture_pageleave: true,
    autocapture: true,
    enable_heatmaps: true,
    capture_exceptions: true,
    disable_session_recording: false,
    respect_dnt: true,
    persistence: 'localStorage',
    opt_out_persistence_by_default: true,
    session_recording: {
      maskAllInputs: true,
      // Explicit false wins over PostHog's remote replay configuration. Never
      // let an operator-side toggle collect contact, signup, or API payloads.
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: redactAnalyticsNetworkRequest,
    },
  },
})

export function initializePostHogIfConsented(): Promise<PostHogClientLike | null> {
  return controller.initializeIfConsented()
}

export function capturePostHog(
  name: string,
  properties?: Record<string, unknown>
): void {
  void controller.capture(name, properties)
}

export function applyPostHogConsent(consent: AnalyticsConsent): void {
  void controller.applyConsent(consent)
}

export function capturePostHogPageview(): void {
  void controller.capturePageview()
}
